import { ArrowUpRight } from "lucide-react";
import type { MarketSignal } from "@/types/marketSignal.type";
import { scrubVendorTerms } from "@/lib/vendorTerms";
import { citationOf } from "./evidenceDisplay";
import { formatShortDate } from "./signalMeta";
import SourceFavicon from "./SourceFavicon";

/** How many sources the card prints before handing over to the dialog. */
const VISIBLE_ROWS = 2;
/** How many of the rest the "more" row names and stacks. */
const MORE_SHOWN = 3;

function sourcesWord(n: number): string {
  return n === 1 ? "source" : "sources";
}

/**
 * The waiting card's evidence column: the first sources a signal was grounded
 * in, each as host, date, headline (when it has a real one) and gist.
 *
 * Every row is a way into the sources dialog, on that source. The head count
 * ("3 SOURCES") is itself the control that opens the dialog on the first, and
 * when there are more sources than the rows shown, the list closes on one
 * compact row naming the rest ("2 more · a.com, b.com") that opens on the first
 * of them. Evidence stays observations-only: fact ids are counted, never
 * opened, because there is nothing behind them to show here.
 */
export default function EvidenceColumn({
  signal,
  onOpenSources,
}: {
  signal: MarketSignal;
  /** Open the sources dialog, on one source when an id is given. */
  onOpenSources: (observationId?: string) => void;
}) {
  const observations = signal.groundingObservations;
  const n = observations.length;
  const facts = signal.groundingFactIds.length;
  const hidden = observations.slice(VISIBLE_ROWS);
  const hiddenHosts = [
    ...new Set(hidden.map((o) => citationOf(o).host).filter((h): h is string => !!h)),
  ].slice(0, MORE_SHOWN);

  return (
    <div className="sig-ev">
      <div className="sig-ev__head">
        <span className="sig-ev__label">Evidence</span>
        {n > 0 ? (
          <button
            type="button"
            className="sig-ev__count"
            aria-haspopup="dialog"
            aria-label={`Open all ${n} ${sourcesWord(n)} for ${signal.title}`}
            onClick={() => onOpenSources(observations[0]?.id)}
          >
            {n} {sourcesWord(n)}
            <ArrowUpRight aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {n > 0 ? (
        <ul className="sig-ev__rows">
          {observations.slice(0, VISIBLE_ROWS).map((o) => {
            const c = citationOf(o);
            return (
              <li key={o.id} className="sig-ev__item">
                <button
                  type="button"
                  className="sig-ev__row"
                  aria-haspopup="dialog"
                  onClick={() => onOpenSources(o.id)}
                >
                  <span className="sig-ev__src">
                    <SourceFavicon host={c.host} size={13} />
                    <span className="sig-ev__host">{c.host ?? "Source"}</span>
                    <span className="sig-ev__date">{formatShortDate(c.observedAt)}</span>
                  </span>
                  {c.title ? (
                    <span className="sig-ev__title" dir="auto">
                      {scrubVendorTerms(c.title)}
                    </span>
                  ) : null}
                  {c.gist ? (
                    <span className="sig-ev__gist" dir="auto">
                      {scrubVendorTerms(c.gist)}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
          {hidden.length > 0 ? (
            <li className="sig-ev__item sig-ev__item--more">
              <button
                type="button"
                className="sig-ev__row sig-ev__more"
                aria-haspopup="dialog"
                onClick={() => onOpenSources(hidden[0].id)}
              >
                <span className="sig-ev__stack" aria-hidden="true">
                  {hidden.slice(0, MORE_SHOWN).map((o) => (
                    <SourceFavicon key={o.id} host={citationOf(o).host} size={12} />
                  ))}
                </span>
                <span className="sig-ev__more-text">
                  {hidden.length} more
                  {hiddenHosts.length > 0 ? ` · ${hiddenHosts.join(", ")}` : ""}
                </span>
              </button>
            </li>
          ) : null}
        </ul>
      ) : (
        <p className="sig-ev__none">
          {facts > 0
            ? `Grounded in ${facts} Brand DNA ${facts === 1 ? "fact" : "facts"}`
            : "No sources recorded."}
        </p>
      )}
    </div>
  );
}
